import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    TextField,
    MenuItem,
    Grid,
    Chip,
    Card,
    CardContent,
    IconButton,
    Collapse
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, FileDownload, FilterList, Clear, Close, PictureAsPdf } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { getInfluencers, getNegotiationCompleted, exportInfluencers, exportFilteredInfluencers } from '../services/api';
import { generateInfluencerPDF } from '../utils/pdfGenerator';
import * as XLSX from 'xlsx';

function SuperAdminDashboard() {
    const [influencers, setInfluencers] = useState([]);
    const [filteredInfluencers, setFilteredInfluencers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        state: '',
        city: '',
        category: '',
        platformType: '',
        socialMediaPlatforms: '',
        influencerType: '',
        engagementRateInstagram: '',
        engagementRateYoutube: '',
        averageViewInstagram: '',
        averageViewYoutube: '',
        dateFrom: '',
        dateTo: ''
    });
    const [uniqueValues, setUniqueValues] = useState({
        states: [],
        cities: [],
        categories: [],
        platformTypes: [],
        socialMediaPlatforms: [],
        engagementRatesInstagram: [],
        engagementRatesYoutube: [],
        averageViewsInstagram: [],
        averageViewsYoutube: []
    });
    const [genderData, setGenderData] = useState({ series: [], options: {} });
    const [ageData, setAgeData] = useState({ series: [], options: {} });
    const [showFilters, setShowFilters] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchInfluencers();
    }, []);

    useEffect(() => {
        if (influencers.length > 0) {
            extractUniqueValues();
        }
        applyFilters();
    }, [influencers, filters]);

    const extractUniqueValues = () => {
        if (!Array.isArray(influencers) || influencers.length === 0) return;

        // Filter data to only include negotiation_status = 1 for dropdown options
        const tableData = influencers.filter(i => i.negotiation_status === 1);

        const states = [...new Set(tableData.map(i => i.state).filter(Boolean))];
        const cities = [...new Set(tableData.map(i => i.city).filter(Boolean))];
        const categories = [...new Set(tableData.map(i => i.category).filter(Boolean))];
        const platformTypes = [...new Set(tableData.map(i => i.platformType).filter(Boolean))];
        const socialMediaPlatforms = [...new Set(tableData.map(i => i.socialMediaPlatforms).filter(Boolean))];

        // Get actual engagement rates from table data
        const engagementRatesInstagram = [...new Set(tableData.map(i => {
            const rate = calculateEngagementRate(i, 'instagram');
            return rate > 0 ? Math.floor(rate * 10) / 10 : null;
        }).filter(Boolean))].sort((a, b) => a - b);

        const engagementRatesYoutube = [...new Set(tableData.map(i => {
            const rate = calculateEngagementRate(i, 'youtube');
            return rate > 0 ? Math.floor(rate * 10) / 10 : null;
        }).filter(Boolean))].sort((a, b) => a - b);

        // Get actual average views from table data
        const averageViewsInstagram = [...new Set(tableData.map(i => {
            const views = calculateAverageViews(i, 'instagram');
            return views > 0 ? Math.floor(views) : null;
        }).filter(Boolean))].sort((a, b) => a - b);

        const averageViewsYoutube = [...new Set(tableData.map(i => {
            const views = calculateAverageViews(i, 'youtube');
            return views > 0 ? Math.floor(views) : null;
        }).filter(Boolean))].sort((a, b) => a - b);

        console.log('Average Views Instagram:', averageViewsInstagram);
        console.log('Average Views YouTube:', averageViewsYoutube);

        setUniqueValues({
            states,
            cities,
            categories,
            platformTypes,
            socialMediaPlatforms,
            engagementRatesInstagram,
            engagementRatesYoutube,
            averageViewsInstagram,
            averageViewsYoutube
        });

        // Gender Distribution - only from table data
        const genderCounts = tableData.reduce((acc, inf) => {
            const gender = inf.gender || 'Unknown';
            acc[gender] = (acc[gender] || 0) + 1;
            return acc;
        }, {});

        const genderSeries = Object.values(genderCounts);
        const genderOptions = {
            labels: Object.keys(genderCounts),
            colors: ['#019383', '#69dc9c', '#f6b042'],
            legend: { position: 'bottom' }
        };

        setGenderData({ series: genderSeries, options: genderOptions });

        // Age Distribution - only from table data
        const ageRanges = {
            '05-17': 0,
            '18-25': 0,
            '26-35': 0,
            '36-45': 0,
            '46+': 0
        };

        tableData.forEach(inf => {
            const age = inf.age;
            if (age >= 5 && age <= 17) ageRanges['05-17']++;
            else if (age >= 18 && age <= 25) ageRanges['18-25']++;
            else if (age >= 26 && age <= 35) ageRanges['26-35']++;
            else if (age >= 36 && age <= 45) ageRanges['36-45']++;
            else if (age >= 46) ageRanges['46+']++;
        });

        const ageSeries = [{
            name: 'Count',
            data: Object.values(ageRanges)
        }];

        const ageOptions = {
            chart: { type: 'bar' },
            xaxis: { categories: Object.keys(ageRanges) },
            colors: ['#019383']
        };

        setAgeData({ series: ageSeries, options: ageOptions });
    };

    const fetchInfluencers = async () => {
        setLoading(true);
        try {
            const response = await getInfluencers();
            console.log('API Response:', response);
            setInfluencers(response.data || response);
        } catch (error) {
            console.error('Failed to fetch influencers:', error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        if (!Array.isArray(influencers)) {
            setFilteredInfluencers([]);
            return;
        }

        let filtered = influencers.filter(influencer => {
            // Only show influencers with negotiation_status = 1
            if (influencer.ocr_status !== 'completed') return false;
            if (influencer.negotiation_status !== 1) return false;

            const matchesState = !filters.state || influencer.state === filters.state;
            const matchesCity = !filters.city || influencer.city === filters.city;
            const matchesCategory = !filters.category || influencer.category === filters.category;
            const matchesPlatformType = !filters.platformType || influencer.platformType === filters.platformType;
            const matchesSocialMediaPlatforms = !filters.socialMediaPlatforms ||
                (filters.socialMediaPlatforms === 'Intagram' && influencer.socialMediaPlatforms?.toLowerCase().includes('instagram')) ||
                (filters.socialMediaPlatforms === 'Youtube' && influencer.socialMediaPlatforms?.toLowerCase().includes('youtube'));

            const matchesInfluencerType = !filters.influencerType ||
                influencer.instagramInfluencerType === filters.influencerType ||
                influencer.youtubeInfluencerType === filters.influencerType;

            const instagramEngagement = calculateEngagementRate(influencer, 'instagram');
            const youtubeEngagement = calculateEngagementRate(influencer, 'youtube');
            const instagramViews = calculateAverageViews(influencer, 'instagram');
            const youtubeViews = calculateAverageViews(influencer, 'youtube');

            const matchesInstagramEngagement = !filters.engagementRateInstagram || instagramEngagement >= Number(filters.engagementRateInstagram);
            const matchesYoutubeEngagement = !filters.engagementRateYoutube || youtubeEngagement >= Number(filters.engagementRateYoutube);
            const matchesInstagramViews = !filters.averageViewInstagram || instagramViews >= Number(filters.averageViewInstagram);
            const matchesYoutubeViews = !filters.averageViewYoutube || youtubeViews >= Number(filters.averageViewYoutube);

            const matchesDateFrom = !filters.dateFrom || new Date(influencer.updatedAt) >= new Date(filters.dateFrom);
            const matchesDateTo = !filters.dateTo || new Date(influencer.updatedAt) <= new Date(filters.dateTo + 'T23:59:59');

            return matchesState && matchesCity && matchesCategory && matchesPlatformType &&
                matchesSocialMediaPlatforms && matchesInfluencerType && matchesInstagramEngagement && matchesYoutubeEngagement &&
                matchesInstagramViews && matchesYoutubeViews && matchesDateFrom && matchesDateTo;
        });
        setFilteredInfluencers(filtered);
    };

    const calculateEngagementRate = (influencer, platform) => {
        if (platform === 'instagram') {
            const likes = Number(influencer.instagramLikes) || 0;
            const comments = Number(influencer.instagramComments) || 0;
            const saves = Number(influencer.instagramSaves) || 0;
            const shares = Number(influencer.instagramShares) || 0;
            const reposts = Number(influencer.instagramReposts) || 0;
            const totalReach = Number(influencer.last10ReelsReach);
            if (!totalReach || isNaN(totalReach)) return 0;
            const instagramNoOfPost = Number(influencer?.instagramNoOfPost) || 10;
            const avgReach = totalReach / instagramNoOfPost;
            return avgReach === 0 ? 0 : ((likes + comments + saves + shares + reposts) / totalReach) * 100;
        } else {
            const likes = Number(influencer.youtubeLikes) || 0;
            const comments = Number(influencer.youtubeComments) || 0;
            const saves = Number(influencer.youtubeSaves) || 0;
            const shares = Number(influencer.youtubeShares) || 0;
            const totalReach = Number(influencer.last10ShortsReach);
            if (!totalReach || isNaN(totalReach)) return "-";
            let youtubeCount;
            if (influencer.youtubeType === 'Shorts') {
                youtubeCount = Number(influencer.youtubeNoOfShorts) || 10;
            } else if (influencer.youtubeType === 'Post') {
                youtubeCount = Number(influencer.youtubeNoOfPost) || 10;
            } else {
                return "-";
            }
            const avgReach = totalReach / youtubeCount;
            return avgReach === 0 ? 0 : ((likes + comments + shares) / totalReach) * 100;
        }
    };

    const calculateAverageViews = (influencer, platform) => {
        if (platform === 'instagram') {
            const totalViews = Number(influencer.last10ReelsViews);
            const instagramNoOfPost = Number(influencer?.instagramNoOfPost) || 100;
            if (isNaN(totalViews) || instagramNoOfPost === 0) return 0;
            return Number((totalViews / instagramNoOfPost).toFixed(2));
        } else {
            const totalViews = Number(influencer.last10ShortsViews);
            let youtubeCount;
            if (influencer.youtubeType === 'Shorts') {
                youtubeCount = Number(influencer.youtubeNoOfShorts) || 10;
            } else if (influencer.youtubeType === 'Post') {
                youtubeCount = Number(influencer.youtubeNoOfPost) || 10;
            } else {
                return "-";
            }
            if (isNaN(totalViews) || youtubeCount === 0) return 0;
            return Number((totalViews / youtubeCount).toFixed(2));
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const clearAllFilters = () => {
        setFilters({
            state: '',
            city: '',
            category: '',
            platformType: '',
            socialMediaPlatforms: '',
            influencerType: '',
            engagementRateInstagram: '',
            engagementRateYoutube: '',
            averageViewInstagram: '',
            averageViewYoutube: '',
            dateFrom: '',
            dateTo: ''
        });
    };

    const columnGroupingModel = [
        {
            groupId: 'basic',
            headerName: 'BASIC INFO',
            headerAlign: 'center',
            children: [{ field: 'socialMediaPlatforms' }, { field: 'sno' }, { field: 'name' }, { field: 'onboarderName' }, { field: 'instagramLink' }, { field: 'youtubeLink' }, { field: 'state' }, { field: 'city' }, { field: 'age' }, { field: 'gender' }, { field: 'language' },]
        },
        {
            groupId: 'business',
            headerName: 'BUSINESS PARAMETER INSTAGRAM',
            headerAlign: 'center',
            children: [
                { field: 'topLocations' },
                { field: 'femaleGenderRatio' },
                { field: 'maleGenderRatio' },
                { field: 'ageRange18_24' },
                { field: 'ageRange25_34' },
                { field: 'ageRange35_44' },
                { field: 'ageRange45_54' },
                { field: 'ageRange55Plus' }
            ]
        },
        {
            groupId: 'financial',
            headerName: 'FINANCIAL INFO',
            headerAlign: 'center',
            children: [
                { field: 'panNumber' },
                { field: 'aadharNumber' },
                { field: 'bankAccountNumber' },
                { field: 'ifscCode' },
                { field: 'holderName' },
                { field: 'bankName' }
            ]
        },
        {
            groupId: 'instagram',
            headerName: 'TECHNICAL PARAMETER INSTAGRAM',
            headerAlign: 'center',
            children: [
                { field: 'instagramInfluencerType' },
                { field: 'totalCostInstagram' },
                { field: 'instagramFollowers' },
                { field: 'engagementRateInstagram' },
                { field: 'averageViewInstagram' },
                { field: 'last10ReelsViews' },
                { field: 'last10ReelsReach' },
                { field: 'instagramLikes' },
                { field: 'instagramComments' },
                { field: 'instagramSaves' },
                { field: 'instagramShares' },
                { field: 'instagramReposts' }
            ]
        },
        {
            groupId: 'businessYoutube',
            headerName: 'BUSINESS PARAMETER YOUTUBE',
            headerAlign: 'center',
            children: [
                { field: 'topLocationsYoutube' },
                { field: 'femaleGenderRatioYoutube' },
                { field: 'maleGenderRatioYoutube' },
                { field: 'ageRange18_24Youtube' },
                { field: 'ageRange25_34Youtube' },
                { field: 'ageRange35_44Youtube' },
                { field: 'ageRange45_54Youtube' },
                { field: 'ageRange55PlusYoutube' }
            ]
        },
        {
            groupId: 'youtube',
            headerName: 'TECHNICAL PARAMETER YOUTUBE',
            headerAlign: 'center',
            children: [
                { field: 'youtubeInfluencerType' },
                { field: 'totalCostYoutube' },
                { field: 'youtubeFollowers' },
                { field: 'engagementRateYoutube' },
                { field: 'averageViewYoutube' },
                { field: 'last10ShortsViews' },
                { field: 'last10ShortsReach' },
                { field: 'youtubeLikes' },
                { field: 'youtubeComments' },
                { field: 'youtubeSaves' },
                { field: 'youtubeShares' }
            ]
        },
        {
            groupId: 'category',
            headerName: 'INFLUENCER PARAMETER',
            headerAlign: 'center',
            children: [
                { field: 'category' }
            ]
        },
        {
            groupId: 'negoation',
            headerName: 'BUDGET PARAMETER',
            headerAlign: 'center',
            children: [
                { field: 'negotiationCostInstagram' },
                { field: 'negotiationCostYoutube' }
            ]
        },
        {
            groupId: 'other',
            headerName: 'OTHER',
            headerAlign: 'center',
            children: [{ field: 'updatedAt' }, { field: 'actions' }]
        }
    ];

    const columns = [
        { field: 'sno', headerName: 'SNO', width: 80 },
        { field: 'socialMediaPlatforms', headerName: 'Channel (IG/YT)', width: 150 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'instagramLink', headerName: 'Profile Link (IN)', width: 180 },
        { field: 'youtubeLink', headerName: 'Profile Link (YT)', width: 180 },
        // { field: 'onboarderName', headerName: 'Onboarder', width: 150 },
        { field: 'state', headerName: 'State', width: 130 },
        { field: 'city', headerName: 'City', width: 130 },
        { field: 'age', headerName: 'Age', width: 120 },
        { field: 'gender', headerName: 'Gender', width: 120 },
        { field: 'topLocations', headerName: 'Top Locations (IN)', width: 180, renderCell: (params) => (<div style={{ whiteSpace: 'pre-line' }}>{params.value}</div>) },
        { field: 'femaleGenderRatio', headerName: 'Female Gender Ratio (IN)', width: 150 },
        { field: 'maleGenderRatio', headerName: 'Male Gender Ratio (IN)', width: 150 },
        { field: 'ageRange18_24', headerName: '18-24 Age Range (IN)', width: 150 },
        { field: 'ageRange25_34', headerName: '25-34 Age Range (IN)', width: 150 },
        { field: 'ageRange35_44', headerName: '35-44 Age Range (IN)', width: 150 },
        { field: 'ageRange45_54', headerName: '45-54 Age Range (IN)', width: 150 },
        { field: 'ageRange55Plus', headerName: '55+ Age Range (IN)', width: 150 },
        {
            field: 'engagementRateInstagram',
            headerName: 'Engagement rate per reach',
            width: 200,
            renderCell: (params) => {
                const rate = calculateEngagementRate(params.row.allData, 'instagram');
                return rate > 0 ? Number(rate.toFixed(2)) : '-';
            }
        },
        { field: 'instagramFollowers', headerName: 'Followers', width: 150 },
        {
            field: 'averageViewInstagram',
            headerName: 'Avg Reels Views',
            width: 110,
            renderCell: (params) => {
                const views = calculateAverageViews(params.row.allData, 'instagram');
                return views > 0 ? views.toLocaleString() : '-';
            }
        },
        {
            field: 'last10ReelsViews',
            headerName: '30 days Reels Views',
            width: 150,
        },
        {
            field: 'last10ReelsReach',
            headerName: 'Instagram Reach',
            width: 150,
        },
        { field: 'topLocationsYoutube', headerName: 'Top Locations (YT)', width: 180, renderCell: (params) => (<div style={{ whiteSpace: 'pre-line' }}>{params.value}</div>) },
        { field: 'femaleGenderRatioYoutube', headerName:'Female Gender Ratio (YT)', width: 150 },
        { field: 'maleGenderRatioYoutube', headerName:'Male Gender Ratio (YT)', width: 150 },
        { field: 'ageRange18_24Youtube', headerName: '18-24 Age Range (YT)', width: 150 },
        { field: 'ageRange25_34Youtube', headerName: '25-34 Age Range (YT)', width: 150 },
        { field: 'ageRange35_44Youtube', headerName: '35-44 Age Range (YT)', width: 150 },
        { field: 'ageRange45_54Youtube', headerName: '45-54 Age Range (YT)', width: 150 },
        { field: 'ageRange55PlusYoutube', headerName: '55+ Age Range (YT)', width: 150 },
        {
            field: 'engagementRateYoutube',
            headerName: 'Engagement rate per reach',
            width: 200,
            renderCell: (params) => {
                const rate = calculateEngagementRate(params.row.allData, 'youtube');
                return rate > 0 ? Number(rate.toFixed(2)) : '-';
            }
        },
        { field: 'youtubeFollowers', headerName: 'Followers', width: 150 },
        {
            field: 'averageViewYoutube',
            headerName: 'Youtube Average',
            width: 110,
            renderCell: (params) => {
                const views = calculateAverageViews(params.row.allData, 'youtube');
                return views > 0 ? views.toLocaleString() : '-';
            }
        },
        {
            field: 'last10ShortsViews',
            headerName: 'Youtube Views',
            width: 150,
        },
        {
            field: 'last10ShortsReach',
            headerName: 'Youtube Reach',
            width: 150,
        },
        { field: 'category', headerName: 'Influencer Content Tag (Eg. Beauty, Lifestyle, Food)', width: 250 },
        {
            field: 'negotiationCostInstagram',
            headerName: 'Negotiation Total Cost (IN)',
            width: 220,
            cellClassName: 'instagram-cell',
            renderCell: (params) => {
                const negotiationCost = params.row.allData?.negotiationCost?.reelCost || 0;
                return negotiationCost > 0 ? `₹${negotiationCost.toLocaleString()}` : '-';
            }
        },
        {
            field: 'negotiationCostYoutube',
            headerName: 'Negotiation Total Cost (YT)',
            width: 220,
            renderCell: (params) => {
                let val;
                if (params.row.allData?.youtubeType === 'Shorts') {
                    val = params.row.allData?.negotiationCost?.youtubeShortsCoast || 0;
                } else {
                    val = params.row.allData?.negotiationCost?.youtubeVideoCost || 0;
                }
                return val > 0 ? `₹${val.toLocaleString()}` : '-';
            }
        },
        {
            field: 'updatedAt',
            headerName: 'Updated At',
            width: 180,
            renderCell: (params) => {
                if (!params.value) return '-';
                const date = new Date(params.value);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                const h = date.getHours();
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const hours = h % 12 || 12;
                const ampm = h >= 12 ? 'PM' : 'AM';
                return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
            }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => navigate(`/influencer/${params.row._id}`)}
                        sx={{ color: '#019383' }}
                    >
                        <Visibility />
                    </IconButton>
                    {/* <IconButton
                        size="small"
                        onClick={() => generatePDF(params.row.allData)}
                        sx={{ color: '#d32f2f' }}
                    >
                        <PictureAsPdf />
                    </IconButton> */}
                </Box>
            )
        }
    ];

    const rows = filteredInfluencers.map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        name: influencer.name,
        updatedAt: influencer.updatedAt,
        state: influencer.state,
        city: influencer.city,
        category: influencer.category,
        location: influencer.location,
        gender: influencer.gender,
        age: influencer.age,
        language: influencer.language,
        category: influencer.category,
        platformType: influencer.platformType,
        socialMediaPlatforms: influencer.socialMediaPlatforms,
        instagramLink: influencer.instagramLink ?? '-',
        instagramFollowers: influencer.instagramFollowers ?? '-',
        youtubeFollowers: influencer.youtubeFollowers ?? '-',
        youtubeLink: influencer.youtubeLink ?? '-',
        number: influencer.contactNumber ?? '-',
        address: influencer.address ?? '-',
        panNumber: influencer.panNumber ?? '-',
        aadharNumber: influencer.aadharNumber ?? '-',
        last10ReelsViews: influencer.last10ReelsViews ?? '-',
        last10ReelsReach: influencer.last10ReelsReach ?? '-',
        instagramLikes: influencer.instagramLikes ?? '-',
        instagramComments: influencer.instagramComments ?? '-',
        instagramSaves: influencer.instagramSaves ?? '-',
        instagramShares: influencer.instagramShares ?? '-',
        instagramReposts: influencer.instagramReposts ?? '-',
        last10ShortsViews: influencer.last10ShortsViews ?? '-',
        last10ShortsReach: influencer.last10ShortsReach ?? '-',
        youtubeLikes: influencer.youtubeLikes ?? '-',
        youtubeComments: influencer.youtubeComments ?? '-',
        youtubeSaves: influencer.youtubeSaves ?? '-',
        youtubeShares: influencer.youtubeShares ?? '-',
        bankAccountNumber: influencer.bankAccountNumber ?? '-',
        ifscCode: influencer.ifscCode ?? '-',
        holderName: influencer.holderName ?? '-',
        bankName: influencer.bankName ?? '-',
        onboarderName: influencer.onboarderId.name ?? '-',
        _id: influencer._id,
        allData: influencer,
        topLocations: influencer?.instagramOcrSchema?.topLocations?.length
            ? influencer.instagramOcrSchema.topLocations
                .map(loc => `${loc.name} - ${loc.percentage}%`)
                .join('\n')
            : '-',
        femaleGenderRatio: influencer?.instagramOcrSchema?.genderDistribution?.female != null
            ? influencer.instagramOcrSchema.genderDistribution.female + '%'
            : '-',

        maleGenderRatio: influencer?.instagramOcrSchema?.genderDistribution?.male != null
            ? influencer.instagramOcrSchema.genderDistribution.male + '%'
            : '-',

        ageRange18_24: influencer?.instagramOcrSchema?.ageDistribution?.[1]?.percentage != null
            ? influencer.instagramOcrSchema.ageDistribution[1].percentage + '%'
            : '-',

        ageRange25_34: influencer?.instagramOcrSchema?.ageDistribution?.[2]?.percentage != null
            ? influencer.instagramOcrSchema.ageDistribution[2].percentage + '%'
            : '-',

        ageRange35_44: influencer?.instagramOcrSchema?.ageDistribution?.[3]?.percentage != null
            ? influencer.instagramOcrSchema.ageDistribution[3].percentage + '%'
            : '-',

        ageRange45_54: influencer?.instagramOcrSchema?.ageDistribution?.[4]?.percentage != null
            ? influencer.instagramOcrSchema.ageDistribution[4].percentage + '%'
            : '-',

        ageRange55Plus: influencer?.instagramOcrSchema?.ageDistribution?.[5]?.percentage != null
            ? influencer.instagramOcrSchema.ageDistribution[5].percentage + '%'
            : '-',
        topLocationsYoutube: influencer?.youtubeOcrSchema?.topLocations?.length
            ? influencer.youtubeOcrSchema.topLocations
                .map(loc => `${loc.name} - ${loc.percentage}%`)
                .join('\n')
            : '-',
        femaleGenderRatioYoutube: influencer?.youtubeOcrSchema?.genderDistribution?.female != null
            ? influencer.youtubeOcrSchema.genderDistribution.female + '%'
            : '-',
        maleGenderRatioYoutube: influencer?.youtubeOcrSchema?.genderDistribution?.male != null
            ? influencer.youtubeOcrSchema.genderDistribution.male + '%'
            : '-',
        ageRange18_24Youtube: influencer?.youtubeOcrSchema?.ageDistribution?.[1]?.percentage != null
            ? influencer.youtubeOcrSchema.ageDistribution[1].percentage + '%'
            : '-',
        ageRange25_34Youtube: influencer?.youtubeOcrSchema?.ageDistribution?.[2]?.percentage != null
            ? influencer.youtubeOcrSchema.ageDistribution[2].percentage + '%'
            : '-',
        ageRange35_44Youtube: influencer?.youtubeOcrSchema?.ageDistribution?.[3]?.percentage != null
            ? influencer.youtubeOcrSchema.ageDistribution[3].percentage + '%'
            : '-',
        ageRange45_54Youtube: influencer?.youtubeOcrSchema?.ageDistribution?.[4]?.percentage != null
            ? influencer.youtubeOcrSchema.ageDistribution[4].percentage + '%'
            : '-',
        ageRange55PlusYoutube: influencer?.youtubeOcrSchema?.ageDistribution?.[5]?.percentage != null
            ? influencer.youtubeOcrSchema.ageDistribution[5].percentage + '%'
            : '-',
    }));

    const generatePDF = (influencer) => {
        generateInfluencerPDF(influencer, 'details');
    };

    const exportToExcel = () => {
        try {
            const data = filteredInfluencers.map((influencer, index) => {
                const igEngagement = calculateEngagementRate(influencer, 'instagram');
                const ytEngagement = calculateEngagementRate(influencer, 'youtube');
                const igAvgView = calculateAverageViews(influencer, 'instagram');
                const ytAvgView = calculateAverageViews(influencer, 'youtube');
                const ocr = influencer?.instagramOcrSchema;
                const topLoc = ocr?.topLocations?.length
                    ? ocr.topLocations.map(loc => `${loc.name} - ${loc.percentage}%`).join(', ')
                    : '-';
                const ytOcr = influencer?.youtubeOcrSchema;
                const topLocYoutube = ytOcr?.topLocations?.length
                    ? ytOcr.topLocations.map(loc => `${loc.name} - ${loc.percentage}%`).join(', ')
                    : '-';
                let ytNegCost;
                if (influencer.youtubeType === 'Shorts') {
                    ytNegCost = influencer.negotiationCost?.youtubeShortsCoast || 0;
                } else {
                    ytNegCost = influencer.negotiationCost?.youtubeVideoCost || 0;
                }

                return {
                    // BASIC INFO
                    'SNO': index + 1,
                    'Channel (IG/YT)': influencer.socialMediaPlatforms || '-',
                    'Name': influencer.name || '',
                    'State': influencer.state || '-',
                    'City': influencer.city || '-',
                    'Profile Link (IN)': influencer.instagramLink || '-',
                    'Profile Link (YT)': influencer.youtubeLink || '-',
                    // FINANCIAL INFO
                    'PAN Number': influencer.panNumber || '-',
                    'Aadhar Number': influencer.aadharNumber || '-',
                    'Bank Account': influencer.bankAccountNumber || '-',
                    'IFSC Code': influencer.ifscCode || '-',
                    'Holder Name': influencer.holderName || '-',
                    'Bank Name': influencer.bankName || '-',
                    // BUSINESS PARAMETER
                    'Age': influencer.age || '-',
                    'Gender': influencer.gender || '-',
                    'Top Locations (IN)': topLoc,
                    'Female Gender Ratio (IN)': ocr?.genderDistribution?.female != null ? ocr.genderDistribution.female + '%' : '-',
                    'Male Gender Ratio (IN)': ocr?.genderDistribution?.male != null ? ocr.genderDistribution.male + '%' : '-',
                    '18-24 Age Range (IN)': ocr?.ageDistribution?.[1]?.percentage != null ? ocr.ageDistribution[1].percentage + '%' : '-',
                    '25-34 Age Range (IN)': ocr?.ageDistribution?.[2]?.percentage != null ? ocr.ageDistribution[2].percentage + '%' : '-',
                    '35-44 Age Range (IN)': ocr?.ageDistribution?.[3]?.percentage != null ? ocr.ageDistribution[3].percentage + '%' : '-',
                    '45-54 Age Range (IN)': ocr?.ageDistribution?.[4]?.percentage != null ? ocr.ageDistribution[4].percentage + '%' : '-',
                    '55+ Age Range (IN)': ocr?.ageDistribution?.[5]?.percentage != null ? ocr.ageDistribution[5].percentage + '%' : '-',
                    // TECHNICAL PARAMETER INSTAGRAM
                    'Engagement Rate per Reach (IN)': igEngagement > 0 ? Number(igEngagement.toFixed(2)) : '-',
                    'Instagram Followers': influencer.instagramFollowers || '-',
                    'Avg Reels Views': igAvgView > 0 ? Math.floor(igAvgView) : '-',
                    '30 days Reels Views.': influencer.last10ReelsViews || '-',
                    'Instagram Reach': influencer.last10ReelsReach || '-',
                    'Instagram Likes': influencer.instagramLikes || '-',
                    'Instagram Comments': influencer.instagramComments || '-',
                    'Instagram Saves': influencer.instagramSaves || '-',
                    'Instagram Shares': influencer.instagramShares || '-',
                    'Instagram Reposts': influencer.instagramReposts || '-',
                    // BUSINESS PARAMETER YOUTUBE
                    'Top Locations (YT)': topLocYoutube,
                    'Female Gender Ratio (YT)': ytOcr?.genderDistribution?.female != null ? ytOcr.genderDistribution.female + '%' : '-',
                    'Male Gender Ratio (YT)': ytOcr?.genderDistribution?.male != null ? ytOcr.genderDistribution.male + '%' : '-',
                    '18-24 Age Range (YT)': ytOcr?.ageDistribution?.[1]?.percentage != null ? ytOcr.ageDistribution[1].percentage + '%' : '-',
                    '25-34 Age Range (YT)': ytOcr?.ageDistribution?.[2]?.percentage != null ? ytOcr.ageDistribution[2].percentage + '%' : '-',
                    '35-44 Age Range (YT)': ytOcr?.ageDistribution?.[3]?.percentage != null ? ytOcr.ageDistribution[3].percentage + '%' : '-',
                    '45-54 Age Range (YT)': ytOcr?.ageDistribution?.[4]?.percentage != null ? ytOcr.ageDistribution[4].percentage + '%' : '-',
                    '55+ Age Range (YT)': ytOcr?.ageDistribution?.[5]?.percentage != null ? ytOcr.ageDistribution[5].percentage + '%' : '-',
                    // TECHNICAL PARAMETER YOUTUBE
                    'YouTube Type': influencer.youtubeType || '-',
                    'Engagement Rate per Reach (YT)': ytEngagement > 0 ? Number(ytEngagement.toFixed(2)) : '-',
                    'YouTube Followers': influencer.youtubeFollowers || '-',
                    'Avg View (YT)': ytAvgView > 0 ? Math.floor(ytAvgView) : '-',
                    'Youtube Views': influencer.last10ShortsViews || '-',
                    'Youtube Reach': influencer.last10ShortsReach || '-',
                    'YouTube Likes': influencer.youtubeLikes || '-',
                    'YouTube Comments': influencer.youtubeComments || '-',
                    'YouTube Saves': influencer.youtubeSaves || '-',
                    'YouTube Shares': influencer.youtubeShares || '-',
                    // INFLUENCER PARAMETER
                    'Category': influencer.category || '-',
                    // BUDGET PARAMETER
                    'Negotiation Total Cost (IN)': influencer.negotiationCost?.reelCost || '-',
                    'Negotiation Total Cost (YT)': ytNegCost > 0 ? ytNegCost : '-',
                    // OTHER
                    'Updated At': influencer.updatedAt ? new Date(influencer.updatedAt).toLocaleDateString('en-GB') : '-',
                };
            });

            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Dashboard Data');
            XLSX.writeFile(workbook, 'super_admin_dashboard_data.xlsx');
        } catch (error) {
            console.error('Failed to export data:', error);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
                    Dashboard Overview
                </Typography>

            </Box>

            {!showFilters ? (
                <Box sx={{ mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<FilterList />}
                        onClick={() => setShowFilters(true)}
                        sx={{ ml: 2, color: 'white', background: '#1a1d29' }}
                    >
                        Filters
                    </Button>
                </Box>
            ) : (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">Filters</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* <Button
                  startIcon={<Clear />}
                  onClick={clearAllFilters}
                  size="small"
                  sx={{ color: '#019383' }}
                >
                  Clear All
                </Button> */}
                                <IconButton
                                    onClick={() => setShowFilters(false)}
                                // sx={{ color: '#019383' }}
                                >
                                    <Close />
                                </IconButton>
                            </Box>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="State"
                                    value={filters.state}
                                    onChange={(e) => handleFilterChange('state', e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {uniqueValues.states.map(state => (
                                        <MenuItem key={state} value={state}>{state}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="City"
                                    value={filters.city}
                                    onChange={(e) => handleFilterChange('city', e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {uniqueValues.cities.map(city => (
                                        <MenuItem key={city} value={city}>{city}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Category"
                                    value={filters.category}
                                    onChange={(e) => handleFilterChange('category', e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {uniqueValues.categories.map(category => (
                                        <MenuItem key={category} value={category}>{category}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Platform Type"
                                    value={filters.socialMediaPlatforms}
                                    onChange={(e) => handleFilterChange('socialMediaPlatforms', e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="Intagram">Instagram</MenuItem>
                                    <MenuItem value="Youtube">Youtube</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Influencer Type"
                                    value={filters.influencerType}
                                    onChange={(e) => handleFilterChange('influencerType', e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="Micro">Micro: 10K-100K</MenuItem>
                                    <MenuItem value="Macro">Macro: 100K-500K</MenuItem>
                                    <MenuItem value="Mid-tier">Mid-tier: 500K-1M</MenuItem>
                                    <MenuItem value="Mega">Mega: 1M-5M</MenuItem>
                                    <MenuItem value="Celeb">Celeb: 5M+</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Engagement Rate (Instagram)"
                                    value={filters.engagementRateInstagram}
                                    onChange={(e) => handleFilterChange('engagementRateInstagram', e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {uniqueValues.engagementRatesInstagram.map(rate => (
                                        <MenuItem key={rate} value={rate}>{rate}%+</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Engagement Rate (Youtube)"
                                    value={filters.engagementRateYoutube}
                                    onChange={(e) => handleFilterChange('engagementRateYoutube', e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {uniqueValues.engagementRatesYoutube.map(rate => (
                                        <MenuItem key={rate} value={rate}>{rate}%+</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Average View (Instagram)"
                                    value={filters.averageViewInstagram}
                                    onChange={(e) => handleFilterChange('averageViewInstagram', e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {uniqueValues.averageViewsInstagram.map(views => (
                                        <MenuItem key={views} value={views}>{views.toLocaleString()}+</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Average View (Youtube)"
                                    value={filters.averageViewYoutube}
                                    onChange={(e) => handleFilterChange('averageViewYoutube', e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {uniqueValues.averageViewsYoutube.map(views => (
                                        <MenuItem key={views} value={views}>{views.toLocaleString()}+</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Date From"
                                    value={filters.dateFrom}
                                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Date To"
                                    value={filters.dateTo}
                                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardContent>
                    <Button
                        variant="contained"
                        startIcon={<FileDownload />}
                        onClick={exportToExcel}
                        sx={{ mb: 2 }}
                    >
                        Export Excel
                    </Button>
                    <DataGrid
                        columns={columns}
                        rows={rows}
                        loading={loading}
                        experimentalFeatures={{ columnGrouping: true }}
                        columnGroupingModel={columnGroupingModel}
                        pageSize={10}
                        disableRowSelectionOnClick
                        autoHeight
                        getRowHeight={() => 'auto'}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f5f5f5',
                                fontWeight: 600
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 600
                            },
                            '& .MuiDataGrid-columnHeader': {
                                borderRight: '1px solid #e0e0e0'
                            },
                            '& .MuiDataGrid-cell': {
                                borderRight: '1px solid #e0e0e0',
                                py: 1
                            },
                            '& .MuiDataGrid-columnGroupHeader': {
                                backgroundColor: '#1a1d29',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '13px',
                                borderRight: '2px solid #fff'
                            },
                            '& .MuiDataGrid-columnGroupHeader .MuiDataGrid-columnHeaderTitle': {
                                color: '#fff',
                                fontWeight: 700
                            }
                        }}
                    />

                    {/* <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            loading={loading}
            autoHeight
            checkboxSelection
            getRowId={(row) => row.id}
          /> */}
                </CardContent>
            </Card>

            <Grid container spacing={3} sx={{ mt: 3 }}>
                <Grid item xs={12} md={4.8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Gender Distribution</Typography>
                            <ReactApexChart
                                options={genderData.options}
                                series={genderData.series}
                                type="pie"
                                width="100%"
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={7.2}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Age Distribution</Typography>
                            <ReactApexChart
                                options={ageData.options}
                                series={ageData.series}
                                type="bar"
                                height={380}
                                width="100%"
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

export default SuperAdminDashboard;